import React, { FC, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../components/nav/LoadingSpinner';
import SubMenuTabBar from 'components/subMenuTabBar/SubMenuTabBar';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import ICONS from '../../constants/icons';
import routes from '../../constants/routes';

import useComponentTypesQuery from '../../hooks/component-types/useComponentTypesQuery';
import useGetTopLabelsMetrics from '../../hooks/metrics/useGetTopLabelsMetrics';

import {
    METRIC_NAMES,
    TopLabelsMetricModel,
    topPerformingMetricsPercent,
} from '../../models/metrics.model';

import TopMetricBaseCard from './TopMetricBaseCard';

const TopPerformingLabels: FC<React.PropsWithChildren<unknown>> = () => {
    const { data: componentTypes, isLoading: isComponentTypesLoading } =
        useComponentTypesQuery();

    const [variantType, setVariantType] = useState<string>(
        componentTypes?.allComponentTypes?.[0] ?? '',
    );
    const [selectedMetric, setSelectedMetric] = useState<METRIC_NAMES>(
        METRIC_NAMES.APP_VISITED_,
    );

    const metricNameForParams = useMemo(() => {
        return topPerformingMetricsPercent.find((item) =>
            item.includes(selectedMetric as string),
        );
    }, [selectedMetric]);

    const { data: topLabelsData, isPending: isLoading } =
        useGetTopLabelsMetrics(metricNameForParams!, variantType);

    const sortedData = useMemo<TopLabelsMetricModel[]>(() => {
        return (topLabelsData ?? [])
            .filter((item) => item.variant_type === variantType)
            .sort((a, b) => {
                const key = 'percent';
                return b[key]! - a[key]!;
            });
    }, [topLabelsData, variantType]);

    const hasData = useMemo<boolean>(
        () => !isLoading && (topLabelsData ?? []).length > 0,
        [isLoading, topLabelsData],
    );

    useEffect(() => {
        if (!variantType && !isComponentTypesLoading && componentTypes) {
            setVariantType(componentTypes?.allComponentTypes?.[0]);
        }
    }, [componentTypes, isComponentTypesLoading, variantType]);

    return (
        <TopMetricBaseCard
            selectedMetric={selectedMetric}
            setSelectedMetric={setSelectedMetric}
        >
            <Variants className="hide-scroll-bar">
                <SubMenuTabBar
                    tabs={(componentTypes?.selectOptions ?? []).map((item) => ({
                        key: item.value as string,
                        name: item.label,
                    }))}
                    currentTab={variantType}
                    setCurrentTab={(variant) =>
                        setVariantType(variant as string)
                    }
                />
            </Variants>

            <Content hasData={hasData} className="hide-scroll-bar">
                {isLoading && <LoadingSpinner styles={{ height: '100px' }} />}
                {hasData ? (
                    sortedData.map((item) => (
                        <Wrapper key={item.label_id}>
                            <DataText
                                target="_blank"
                                rel="noopener"
                                to={routes.labelManagement}
                            >
                                {item.label_name}
                            </DataText>

                            <Text>{`${item.percent?.toFixed(2)}%`}</Text>
                        </Wrapper>
                    ))
                ) : !isLoading ? (
                    <EmptyStateContainer>
                        <EmptyState>
                            <ICONS.componentsLabels
                                size={40}
                                color={aampeTheme.colors.neutral09}
                            />
                            <EmptyStateText>
                                Launch a labelled message to see your
                                top-performing labels
                            </EmptyStateText>
                        </EmptyState>
                    </EmptyStateContainer>
                ) : null}
            </Content>
        </TopMetricBaseCard>
    );
};
const Variants = styled.div(() => ({
    display: 'flex',
    overflow: 'auto',
    whiteSpace: 'nowrap',
    gap: aampeTheme.spacing(4),
}));

const Content = styled.div<{ hasData: boolean }>(({ hasData }) => ({
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
    marginTop: hasData ? aampeTheme.spacing4(4) : 0,
    overflowY: 'auto',
}));

const Wrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: aampeTheme.colors.backgroundWG01,
    padding: aampeTheme.spacing4(3, 4),
    borderRadius: aampeTheme.borderRadius.medium,
});

const DataText = styled(Link)({
    width: '70%',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textDecoration: 'none',
    color: aampeTheme.colors.neutral09,
    fontWeight: aampeTheme.weights.heavy,
});

const Text = styled.span({
    color: aampeTheme.colors.textAccessibleWG01,
});

const EmptyStateContainer = styled.div({
    borderRadius: aampeTheme.borderRadius.medium,
    border: `1px solid ${aampeTheme.colors.interactiveWG03}`,
    background: aampeTheme.colors.backgroundWG01,
    height: '100%',
    padding: aampeTheme.spacing4(2),
});

const EmptyState = styled.div({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    rowGap: aampeTheme.spacing4(2),
    justifyContent: 'center',
    alignItems: 'center',
});

const EmptyStateText = styled.span({
    color: aampeTheme.colors.neutral09,
    fontWeight: aampeTheme.weights.light,
    textAlign: 'center',
});

export default TopPerformingLabels;
